/**
 * 11 - Create a Diversion (03 §2): lie low until the signal, then fight north to the border
 * and be there when the clock runs out - Recall is disabled. No compound: the goal band is
 * the densest belt in the game, two wire lines with staggered gaps, turret bunkers, mines
 * and proximity bombs between them, and the border itself beyond.
 */

import { siteEmplacement } from '../emplacements';
import { buyMinefield, buyProximityPost } from '../fieldworks';
import { buildRun, reserveGaps } from '../obstacles';
import { LINES } from '../tuning';
import { checker, countKind, guardsAbout, insideRect, ruleOf } from './helpers';
import type { ArchetypeSpec } from './types';

/** Depth of the border strip the ranger must be standing in at clock zero. */
const BORDER = 14;

export const diversion: ArchetypeSpec = {
  id: 'diversion',
  name: 'CREATE A DIVERSION',
  nativeBiome: 'temperate',
  baseThreat: 6,
  lengthTiles: 180,
  clockScale: 1,
  startAlert: 0,
  garrison: 'regular',
  perimeter: 'none',
  sealed: false,
  layout: 'border',
  size: { w: [88, 88], d: [30, 40] },
  river: false,
  patrolLoop: 'inside',
  breach: { min: 0, max: 0 },

  build(g) {
    const { ctx, map, rng } = g;
    const roadX = ctx.plan.roadEnd.x;
    const quietX = ctx.plan.lines[ctx.plan.lines.length - 1]?.quietX ?? ctx.sizeX - roadX;
    const mid = Math.round((roadX + quietX) / 2);
    // Both lines open for the road; their second gaps are staggered so there is no straight run through.
    const road = { x: roadX, width: LINES.roadGap };
    const south = { z: g.rect.z1 - 4, gaps: [road, { x: Math.round(quietX), width: LINES.extraGap }] };
    const north = { z: g.rect.z0 + 8, gaps: [road, { x: mid, width: LINES.extraGap }] };
    for (const line of [south, north]) {
      reserveGaps(map, line.z, line.gaps);
      buildRun(map, 'wire', line.z, 0, ctx.sizeX, line.gaps);
    }

    const goalBand = ctx.bands.length - 1;
    const bunkers = 2 + (g.threat >= 7 ? 1 : 0);
    let sited = 0;
    // The road gap first, then the middle of the line. A turret sees all round, so the quiet gap only gets one
    // where it cannot overlook the end of the ravine (quietSide.ts); failing that the third goes by the road too.
    const covers = [south.gaps[0], { x: mid, width: 0 }, south.gaps[1], south.gaps[0]];
    for (let i = 0; i < bunkers; i++) {
      for (let k = 0; k < covers.length; k++) {
        const at = covers[(i + k) % covers.length];
        if (!at || !siteEmplacement(ctx, rng, 'turretBunker', { x: at.x, z: south.z }, g.elite)) continue;
        sited++;
        break;
      }
    }
    if (sited < 2) map.problems.push('diversion: no room for the turret bunkers');
    const last = north.gaps[1];
    if (last) siteEmplacement(ctx, rng, 'mannedTurret', { x: last.x, z: north.z }, g.elite);

    for (const line of [south, north]) {
      for (const gap of line.gaps) ctx.coverPoints.push({ x: gap.x, z: line.z, band: goalBand, used: true });
    }
    for (let i = 0; i < 3 + rng.int(3); i++) buyProximityPost(ctx, rng, goalBand, Infinity);
    for (let i = 0; i < 2 + rng.int(3); i++) buyMinefield(ctx, rng, goalBand, Infinity);
    guardsAbout(g, 3);

    const zone = { x0: 0, z0: 0, x1: ctx.sizeX, z1: BORDER };
    return {
      goalZone: zone,
      objective: {
        kind: 'reachZone', label: 'REACH THE BORDER', targetPropIds: [], minFraction: 1, zone,
        rules: [
          // The beep comes 15 s after touchdown, or later: the briefing gives the time.
          { type: 'signal', atSeconds: 15 + 15 * rng.int(6), failIfQuietForSeconds: 90 },
          { type: 'recall', mode: 'disabled' },
        ],
      },
    };
  },

  asserts(def) {
    const { check, out } = checker('diversion');
    const zone = def.objective.zone;
    check(def.objective.kind === 'reachZone' && zone !== undefined, 'objective must be to reach a zone');
    if (zone) {
      check(zone.z0 <= 0 && zone.x0 <= 0 && zone.x1 >= def.sizeX, 'the border zone must span the north edge');
      check(insideRect(zone, def.pickup.x, def.pickup.z), 'the Osprey must come to the border');
    }
    check(ruleOf(def, 'recall')?.mode === 'disabled', 'early Recall must be disabled');
    const signal = ruleOf(def, 'signal');
    check(
      signal !== undefined && signal.atSeconds >= 15 && signal.failIfQuietForSeconds === 90,
      'needs the lie-low signal (quiet 60 s + 30 s fails)',
    );
    check(countKind(def, 'turretBunker') >= 2, 'the border needs at least two turret bunkers');
    const wire = def.props.filter((p) => p.kind === 'wire' && p.z < def.sizeZ * 0.25);
    check(new Set(wire.map((p) => p.z)).size >= 2, 'the border needs two wire lines');
    return out;
  },
};
